/******************************************************************************
 *                                                                            *
 *  Copyright (c) 2018, 2025 by AsterionDB Inc.                               *
 *                                                                            *
 *  All rights reserved.  No part of this work may be reproduced or otherwise *
 *  incorporated into other works without the express written consent of      *
 *  AsterionDB Inc.                                                           *
 *                                                                            *
 *****************************************************************************/

import React from 'react'
import { Col, FormGroup, Label, Button } from 'reactstrap'
import { Navigate } from 'react-router-dom'

import PasswordWidget from '../widgets/PasswordWidget.js'

import {AppContext} from '../AppContext';

// TODO: the password inputs and zxcvbn loading is done in Signup, ResetPassword and here (ChangePassword) 
// (anywhere else? ChangePassword.js.old)... we should abstract, if possible, the password input into a single sub-component
// UPDATE: THIS FILE *is* an abstraction used in ManageProfile, but it would have to use some props to change its behavior
// when being used by ResetPassword or Signup (includes what API to call, whether or not to require the old password, 
// success/error messages, whether or not to redirect (and having a modal popup vs. notification widget, etc))

class ResetPassword extends React.Component
{
  redirectPauseSeconds = 5;
  redirectTimer = null;

  async componentDidMount()
  {
    // because we only expect a single argument,
    // instead of parsing multiple query arguments, 
    // we can actually treat this as a security
    // enhancement and ignore requests that aren't
    // what we expect
    if (window.location.search.substring(0, 1) !== '?'
     || /[^0-9a-zA-Z]/.test(window.location.search.substring(1)))
    {
      // redirect back to login page
      // to external URI:
      // window.location.href = this.redirectURI;
      // can use history.push() if you want the current page to stay in the browser history
      this.props.history.replace(window.redirectURI);
      return;
    }

    // by taking the entire query string without checking, if the user
    // adds anything funny, they will just invalidate their request
    // (as long as the DB passes this entire string to its decrypt function)
    var confirmationToken = window.location.search.substring(1);
    let bodyData = {};
    bodyData.confirmationToken = confirmationToken;
    
    // check if what appears to be a valid token *really* is valid
    let result = await this.context.dbTwig.callRestAPI('icam', 'checkConfirmationToken', bodyData);
    if ('apiError' === result.status && this.context.dbTwig.InvalidConfirmationData === result.errorCode) 
      this.props.history.replace(window.redirectURI);
    if ('success' !== result.status) return this.context.dbTwig.apiErrorHandler(result, 'Unable to confirm validation data');

    window.loadZxcvbn();
  }

  componentWillUnmount()
  {
    if (this.redirectTimer) clearTimeout(this.redirectTimer);
  }

  constructor(props)
  {
    super();

    this.state =
    {
      newPasswordScore: null,
      newPassword: '',
      newPasswordValid: undefined,
      newPasswordType: 'password',
      confirm: '',
      confirmDisabled: true,
      confirmValid: undefined,
      confirmType: 'password',
      confirmFB: null,
      newPasswordFB: null,
      buttonDisabled: true,
      passwordReset: false
    }

    this.resetPassword = this.resetPassword.bind(this);
    this.redirect = this.redirect.bind(this);
  }

  redirect()
  {
    this.redirectTimer = null;
    this.setState({passwordReset: true});
  }

  render()
  {
    if (this.state.passwordReset) return (<Navigate to={{pathname: '/login', replace: true}} />);

    return(
      <div>
        <FormGroup row>
          <Label for="newPassword" sm="2">New Password</Label>
          <Col sm="3" >
            <PasswordWidget onChange={this.setPasswordVar.bind(this, 'newPassword')} value={this.state.newPassword}
              passwordValid={this.state.newPasswordValid} passwordFB={this.state.newPasswordFB} 
              placeholder='Enter your new password' zxcvbn={this.state.newPasswordScore} id='newPassword'
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label for="confirmation" sm="2">Confirmation Password</Label>
          <Col sm="3" >
            <PasswordWidget onChange={this.setPasswordVar.bind(this, 'confirm')} value={this.state.confirm}
              passwordValid={this.state.confirmValid} passwordFB={this.state.confirmFB} 
              placeholder='Enter your new password again' id='confirmation'
              disabled={this.state.confirmDisabled}
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Col sm={{ size: 3, offset: 2 }}>
            <Button id="changePasswordButton" onClick={this.resetPassword} disabled={this.state.buttonDisabled} color="primary">Reset Password</Button>
          </Col>
        </FormGroup>
      </div>
    );
  }

  async resetPassword(event)
  {
    this.setState({buttonDisabled: true});

    let bodyData = {};
    bodyData.newPassword = this.state.newPassword;

    // by taking the entire query string without checking, if the user
    // adds anything funny, they will just invalidate their request
    // (as long as the DB passes this entire string to its decrypt function)
    var confirmationToken = window.location.search.substring(1);
    bodyData.confirmationToken = confirmationToken;
    
    let result = await this.context.dbTwig.callRestAPI('icam', 'resetPassword', bodyData);
    if ('success' !== result.status)
    {
      this.setState({buttonDisabled: false});
      return this.context.dbTwig.apiErrorHandler(result, 'Error changing password');
    } 

    this.context.postNotification('success', 
          'Your password has been changed. Please use your new password to log in from now on. You will be redirected to the login page momentarily.');
    this.redirectTimer = setTimeout(this.redirect, this.redirectPauseSeconds * 1000);
  }

  setPasswordVar(field, value)
  {
    let newPasswordValid = this.state.newPasswordValid;
    let confirmValid = this.state.confirmValid;

    let newPassword = this.state.newPassword;
    let confirm = this.state.confirm;

    let confirmDisabled = this.state.confirmDisabled;

    let newPasswordFB = this.state.newPasswordFB;
    let confirmFB = this.state.confirmFB;

    let buttonDisabled = this.state.buttonDisabled;
    let newPasswordScore = this.state.newPasswordScore;

    switch (field)
    {
      case 'newPassword':
        newPassword = value;
        newPasswordScore = window.getPasswordScore(newPassword);
        if ('' === newPassword)
        {
          newPasswordScore = null;
          newPasswordValid = false;
          confirm = '';
          confirmDisabled = true;
          confirmValid = true;
          buttonDisabled = true;
          newPasswordFB = 'You must enter a valid and strong new password to continue.';
        }
        else if (newPasswordScore.score < this.context.websiteProfile.passwordStrength)
        {
          newPasswordFB = 'You must enter a stronger password.  The meter has to go green.';
          newPasswordValid = false;
          buttonDisabled = true;
        }
        else
        {
          confirmDisabled = false;
          newPasswordValid = true;
          newPasswordFB = null;
        }

        break;

      case 'confirm':
        confirm = value
        if ('' === confirm)
        {
          confirmValid = false;
          buttonDisabled = true;
          confirmFB = 'You must confirm your new password by typing it in again.';
        }
        else
        {
          confirmValid = true;
          confirmFB = null;
        }
        break;

      default:
        break;
    }

    if ('' !== newPassword && '' !== confirm)
    {
      if (newPassword !== confirm)
      {
        confirmFB = 'Your new and confirmation passwords do not match!';
        confirmValid = false;
        buttonDisabled = true;
      }
      else
      {
        confirmFB = null;
        newPasswordValid = true;
        confirmValid = true;
        buttonDisabled = false;
      }
    }
     
    this.setState(
    {
      newPassword: newPassword,
      newPasswordValid: newPasswordValid,
      newPasswordFB: newPasswordFB,
      confirm: confirm,
      confirmDisabled: confirmDisabled,
      confirmValid: confirmValid,
      confirmFB: confirmFB,
      newPasswordScore: newPasswordScore,
      buttonDisabled: buttonDisabled
    });
  }
}

ResetPassword.contextType = AppContext;

export default ResetPassword;